import React from 'react';
import Icon from 'src/components/common/Icon';
import { PortfolioProperty } from 'src/redux/user_portfolio/user_portfolio.types';
import { propertyTitle, SlotCategory } from './assignmentsHelpers';
import {
    InlineSpinner,
    ModalActions,
    ModalBody,
    ModalCard,
    ModalClose,
    ModalOutlineButton,
    ModalOverlay,
    ReplaceCompare,
    ReplaceRow,
    ReplaceThumb,
} from './VisibilityAssignment.styles';

/** Etiqueta (plural) de la categoría del slot, para el texto del modal. */
const CATEGORY_LABEL: Record<SlotCategory, string> = {
    prime: 'Prime',
    destacado: 'Destacados',
};

interface ReplaceConfirmModalProps {
    open: boolean;
    /** Categoría del slot ocupado al que se arrastra la nueva propiedad. */
    category: SlotCategory | null;
    /** Número visible del slot (posición 1-based dentro de los ocupados). */
    slotNumber: number | null;
    /** Propiedad actualmente asignada al slot (saliente); puede no resolverse. */
    currentProperty?: PortfolioProperty;
    /** Id de la propiedad actual, para el ID cuando no se resuelve el objeto. */
    currentPropertyId: number | null;
    /** Propiedad libre que se va a asignar (entrante). */
    newProperty?: PortfolioProperty;
    /** Id de la propiedad nueva, para el ID cuando no se resuelve el objeto. */
    newPropertyId: number | null;
    loading: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}

/**
 * Confirmación para reemplazar la propiedad de un slot ocupado por una propiedad
 * libre arrastrada encima. Muestra la propiedad ACTUAL (tachada, saliente) y la
 * NUEVA (entrante) antes de ejecutar el escenario SWAP.
 */
const ReplaceConfirmModal = ({
    open,
    category,
    slotNumber,
    currentProperty,
    currentPropertyId,
    newProperty,
    newPropertyId,
    loading,
    onCancel,
    onConfirm,
}: ReplaceConfirmModalProps) => {
    if (!open) return null;

    const rowCategory: SlotCategory = category ?? 'destacado';
    const categoryLabel = category ? CATEGORY_LABEL[category] : '';
    const slotText = slotNumber != null ? String(slotNumber).padStart(2, '0') : '';
    const currentName = currentProperty
        ? propertyTitle(currentProperty)
        : `Propiedad ${currentPropertyId ?? ''}`;
    const newName = newProperty ? propertyTitle(newProperty) : `Propiedad ${newPropertyId ?? ''}`;

    return (
        <ModalOverlay
            data-id="replace-overlay"
            onClick={(e) => {
                if (e.target === e.currentTarget && !loading) onCancel();
            }}
        >
            <ModalCard>
                <ModalClose
                    type="button"
                    data-id="replace-close"
                    aria-label="Cerrar"
                    onClick={onCancel}
                    disabled={loading}
                >
                    <Icon icon="pcom-icon-close" size={16} />
                </ModalClose>
                <ModalBody>
                    <h4>Reemplazar propiedad</h4>
                    <p>
                        Vas a sustituir la propiedad asignada a este slot {categoryLabel}
                        {slotText ? ` · ${slotText}` : ''}.
                    </p>

                    <ReplaceCompare>
                        <ReplaceRow
                            $variant="current"
                            $category={rowCategory}
                            data-id="replace-current"
                        >
                            <span className="role">Actual</span>
                            <div className="prop">
                                <ReplaceThumb aria-hidden>
                                    <Icon icon="pcom-icon-home" size={18} />
                                </ReplaceThumb>
                                <div className="info">
                                    <span className="name">{currentName}</span>
                                    <span className="id">ID {currentPropertyId ?? ''}</span>
                                </div>
                            </div>
                        </ReplaceRow>
                        <ReplaceRow $variant="new" $category={rowCategory} data-id="replace-new">
                            <span className="role">Nueva</span>
                            <div className="prop">
                                <ReplaceThumb aria-hidden>
                                    <Icon icon="pcom-icon-home" size={18} />
                                </ReplaceThumb>
                                <div className="info">
                                    <span className="name">{newName}</span>
                                    <span className="id">ID {newPropertyId ?? ''}</span>
                                </div>
                            </div>
                        </ReplaceRow>
                    </ReplaceCompare>

                    <ModalActions>
                        <ModalOutlineButton
                            type="button"
                            data-id="replace-cancel"
                            onClick={onCancel}
                            disabled={loading}
                        >
                            Cancelar
                        </ModalOutlineButton>
                        <ModalOutlineButton
                            type="button"
                            data-id="replace-confirm"
                            onClick={onConfirm}
                            disabled={loading}
                        >
                            {loading && <InlineSpinner aria-hidden />}
                            Reemplazar
                        </ModalOutlineButton>
                    </ModalActions>
                </ModalBody>
            </ModalCard>
        </ModalOverlay>
    );
};

export default ReplaceConfirmModal;
